import {
  Injectable,
  Inject,
  autobind,
  forwardRef,
} from '@kaokei/use-vue-service';
import { DaoService } from '../dao.service';

import type { QuestionDto, QuestionsDto } from '@/types';

import { ExamService } from '@/services/exam/exam.service';

import {
  IS_QUESTION_TYPE_CHOICE,
  IS_QUESTION_TYPE_PROGRAM,
  IS_QUESTION_TYPE_FILL_BLANK,
  IS_QUESTION_TYPE_FILE,
  getTagName,
  getToken,
  domains,
  checkIde,
  saveIde,
  closeIde,
} from '@/utils';

import { UserService } from '@/services/user.service';

// 试卷信息和用户答案和用户信息

@Injectable()
export class QuestionService {
  @Inject(forwardRef(() => ExamService))
  public examService!: ExamService;

  /**
   * 当前选中的题目
   *
   * @type {QuestionDto}
   * @memberof ExamService
   */
  public question!: QuestionDto;

  /**
   * 当前选择的题目的索引
   *
   * @memberof ExamService
   */
  public currentIndex = 1;

  /**
   * 当前选中的题目类型
   *
   * @type {QuestionsDto}
   * @memberof ExamService
   */
  public currentQuestionType!: QuestionsDto;

  /**
   * iframe保存时的动画开关
   *
   * @memberof QuestionService
   */
  public ideIframeSaving = false;

  /**
   * 显示ide界面
   *
   * @memberof QuestionService
   */
  public showIde = false;

  /**
   * 第一次显示ide界面，只有第一次显示ide才会加载iframe
   * 后续不出处罚iframe的重复加载
   *
   * @memberof QuestionService
   */
  public firstTimeShowIde = false;

  public ideIframeDom?: HTMLIFrameElement;

  /**
   * 控制作品介绍弹窗的显示字段
   *
   * @memberof QuestionService
   */
  public workProfileDialogVisible = false;

  public iframeRefreshTag = Date.now();

  public get isProgram() {
    if (this.question && this.question.questionTypeId) {
      return IS_QUESTION_TYPE_PROGRAM(this.question.questionTypeId);
    }
    return false;
  }

  /**
   * 自由创作题
   *
   * @readonly
   * @memberof QuestionService
   */
  public get isProgramCreation() {
    if (this.question && this.question.questionTypeId) {
      return [6, 8].includes(this.question.questionTypeId);
    }
    return false;
  }

  public get isChoice() {
    if (this.question && this.question.questionTypeId) {
      return IS_QUESTION_TYPE_CHOICE(this.question.questionTypeId);
    }
    return false;
  }

  public get isFillBlank() {
    if (this.question && this.question.questionTypeId) {
      return IS_QUESTION_TYPE_FILL_BLANK(this.question.questionTypeId);
    }
    return false;
  }

  // 是附件题
  public get isAttachment() {
    if (this.question && this.question.questionTypeId) {
      return IS_QUESTION_TYPE_FILE(this.question.questionTypeId);
    }
    return false;
  }

  public get isFirstQuestion() {
    if (this.question) {
      return (
        this.question.id === this.flattedQuestions[0].id ||
        (this.examService.answerOrderLimit &&
          this.examService.hasFinishedObjective &&
          (!IS_QUESTION_TYPE_PROGRAM(this.question.questionTypeId) ||
            this.isFirstProgramQuestion))
      );
    }
    return false;
  }

  public get isFirstProgramQuestion() {
    if (this.question) {
      const firstProgram = this.flattedQuestions.find(item =>
        IS_QUESTION_TYPE_PROGRAM(item.questionTypeId)
      );
      return firstProgram && this.question.id === firstProgram?.id;
    }
    return false;
  }

  public get isLastQuestion() {
    if (this.question) {
      const len = this.flattedQuestions.length;
      return this.question.id === this.flattedQuestions[len - 1].id;
    }
    return false;
  }

  public get flattedQuestions() {
    if (this.examService.exam) {
      return this.examService.exam.questionsDtoList
        .map(item => item.questionDtoList)
        .reduce((acc, cur) => acc.concat(cur), []);
    }
    return [];
  }

  /**
   * 当前题目的参考答案
   *
   * @readonly
   * @memberof QuestionService
   */
  public get currentQuestionPaperAnswer() {
    if (this.question) {
      const answer = this.examService.getPaperAnswerByQuestionId(
        this.question.id
      );
      return answer?.answerContent;
    }
  }

  public get currentQuestionMarkRecordTableData() {
    if (this.question) {
      const markRecord = this.examService.getMarkRecordByQuestionId(
        this.question.id
      );

      if (!markRecord) return;
      const markRecordList = markRecord.markRecordList;

      if (!markRecordList || !markRecordList.length) return;

      const firstItemList = markRecordList[0].itemList;

      const markRuleNameData = firstItemList
        ? firstItemList.map(item => item.ruleName)
        : [];

      const totalPaperScore = firstItemList
        ? firstItemList.reduce((accr, item) => accr + item.ruleScore, 0)
        : this.question.totalScore;

      const teacherData = markRecordList.map(item => {
        const tdata = {
          id: item.id,
          name: item.markUserName,
          start: item.markStartTime,
          end: item.markEndTime,
          totalScore: item.markTotalScore,
          totalPaperScore: totalPaperScore,
        };

        item.itemList?.forEach((obj, index) => {
          (tdata as any)[index] = obj;
        });

        return tdata;
      });

      return {
        teacherData,
        markRuleNameData,
      };
    }
  }

  public get ideIframeUrl() {
    const tag = getTagName();
    const contestName = ''; // 新系统没有这个参数
    const {
      questionTypeId,
      id: questionId,
      title: questionName,
    } = this.question;
    const token = getToken();
    const mode = this.examService.answerMode ? 'teacher' : 'student';
    const readOnly = this.examService.answerMode ? 1 : 0;
    const host = encodeURIComponent(location.origin);
    const examId = this.examService.exam?.examId || 0;
    const answerPaperId = this.examService.answerPaperId || 0;
    const userExamId = this.examService.userExamId;
    const userId = this.userService.user?.userId || 0;

    const userAnswer = this.examService.getUserAnswerByQuestionId(questionId);
    const sb3File = userAnswer?.userAnswer || this.question?.projectUrl;

    if (
      !userExamId ||
      !questionTypeId ||
      !questionId ||
      !token ||
      !examId ||
      !answerPaperId ||
      !userId
    ) {
      return;
    }

    if (questionTypeId === 1) {
      // OJ题
      if (this.examService.answerMode) {
        return `${domains.contestIdeDomain}/match-online-judge-ide/?host=${host}&userExamId=${userExamId}&mode=${mode}&examId=${examId}&questionId=${questionId}&answerId=${answerPaperId}&studentId=${userId}&token=${token}&questionTypeId=${questionTypeId}&readOnly=${readOnly}&contest=${contestName}&tag=${tag}&title=${questionName}&_Refresh=${this.iframeRefreshTag}`;
      } else {
        return `${domains.contestIdeDomain}/match-online-judge-ide/?host=${host}&userExamId=${userExamId}&mode=${mode}&examId=${examId}&questionId=${questionId}&answerId=${answerPaperId}&studentId=${userId}&token=${token}&questionTypeId=${questionTypeId}&readOnly=${readOnly}&contest=${contestName}&tag=${tag}&_Refresh=${this.iframeRefreshTag}`;
      }
    } else if ([5, 6, 7].includes(questionTypeId)) {
      // 编程题和自由创作题
      if (this.examService.answerMode) {
        return `${domains.contestIdeDomain}/contestide/?host=${host}&userExamId=${userExamId}&mode=${mode}&examId=${examId}&questionId=${questionId}&answerPaperId=${answerPaperId}&userId=${userId}&_hs=${token}&questionTypeId=${questionTypeId}&readOnly=${readOnly}&url=${sb3File}&title=${questionName}&_Refresh=${this.iframeRefreshTag}`;
      } else {
        return `${domains.contestIdeDomain}/contestide/?host=${host}&userExamId=${userExamId}&mode=${mode}&examId=${examId}&questionId=${questionId}&answerPaperId=${answerPaperId}&userId=${userId}&_hs=${token}&questionTypeId=${questionTypeId}&readOnly=${readOnly}&_Refresh=${this.iframeRefreshTag}`;
      }
    } else if ([8].includes(questionTypeId)) {
      // 编程题和自由创作题
      if (this.examService.answerMode) {
        return `${
          domains.contestPythonIdeDomain
        }/ide-python-b2c/?mode=create&host=${host}&reqDomain=${encodeURIComponent(
          domains.apiDomain
        )}&userExamId=${userExamId}&examId=${examId}&questionId=${questionId}&answerPaperId=${answerPaperId}&userId=${userId}&_hs=${token}&questionTypeId=${questionTypeId}&readOnly=${readOnly}&url=${sb3File}&title=${questionName}&_Refresh=${
          this.iframeRefreshTag
        }&ideType=isCompetition`;
      } else {
        return `${
          domains.contestPythonIdeDomain
        }/ide-python-b2c/?mode=create&host=${host}&reqDomain=${encodeURIComponent(
          domains.apiDomain
        )}&userExamId=${userExamId}&examId=${examId}&questionId=${questionId}&answerPaperId=${answerPaperId}&userId=${userId}&_hs=${token}&questionTypeId=${questionTypeId}&readOnly=${readOnly}&_Refresh=${
          this.iframeRefreshTag
        }&ideType=isCompetition`;
      }
    } else {
      return '';
    }
  }

  constructor(public daoService: DaoService, public userService: UserService) {}

  @autobind
  public showIdeEvent() {
    if (this.firstTimeShowIde === false) {
      this.firstTimeShowIde = true;
    }
    this.showIde = true;
  }

  @autobind
  public hideIdeEvent() {
    this.trySaveIde().then(() => {
      this.examService.syncQuestionStatus();
      if (!this.showIde) {
        if (this.ideIframeDom) {
          // 如果ide已经关闭了
          //  需要通知ide那边暂停运行中的sb3文件
          closeIde(this.ideIframeDom);
        }
      }
    });
  }

  public trySaveIde() {
    if (this.ideIframeDom) {
      return checkIde(this.ideIframeDom).then(res => {
        if (res) {
          if (this.ideIframeDom) {
            this.ideIframeSaving = true;
            return saveIde(this.ideIframeDom)
              .then(() => {
                this.ideIframeSaving = false;
                this.showIde = false;
              })
              .catch(e => {
                this.ideIframeSaving = false;
                // add sentry mark
                // console.log('setTag', 'api', 'saveIde');
              });
          }
        } else {
          this.showIde = false;
        }
      });
    } else {
      return Promise.resolve();
    }
  }

  public setIdeIframe(iframe: HTMLIFrameElement) {
    this.ideIframeDom = iframe;
  }

  @autobind
  public selectQuestion(q: QuestionDto) {
    if (this.question && this.question.id === q.id) return;
    this.ideIframeDom = undefined;
    this.question = q;
    this.flattedQuestions.forEach(item => (item.selected = false));
    this.question.selected = true;
    this.examService.exam.questionsDtoList.forEach(item => {
      if (
        item.questionTypeId === q.questionTypeId ||
        item.questionTypeList?.includes(q.questionTypeId)
      ) {
        this.currentQuestionType = item;
        this.currentIndex =
          item.questionDtoList.findIndex(i => i.id === q.id) + 1;
      }
    });
    this.examService.getMarkRecordFromServer(q);
  }

  /**
   * 默认选择题目：
   * 1. 没有启用 先做主观题的配置，则默认选择第一题
   * 2. 启用了先做主观题的配置，如果已经开始了做主观题，就要直接跳转到第一到主观题
   * @memberof QuestionService
   */
  public selectDefault() {
    if (
      this.examService.answerOrderLimit &&
      this.examService.hasFinishedObjective
    ) {
      for (let i = 0; i < this.examService.exam.questionsDtoList.length; i++) {
        const questionsDtoList = this.examService.exam.questionsDtoList[i];
        if (IS_QUESTION_TYPE_PROGRAM(questionsDtoList.questionTypeId)) {
          this.selectQuestion(questionsDtoList.questionDtoList[0]);
          return;
        }
      }
    } else {
      this.selectQuestion(this.flattedQuestions[0]);
    }
  }

  @autobind
  public goToPrevQuestion() {
    if (this.question) {
      for (let i = 1; i < this.flattedQuestions.length; i++) {
        const current = this.flattedQuestions[i];
        if (current.id === this.question.id) {
          this.selectQuestion(this.flattedQuestions[i - 1]);
          return;
        }
      }
    }
  }

  @autobind
  public goToNextQuestion() {
    if (this.question) {
      for (let i = 0; i < this.flattedQuestions.length - 1; i++) {
        const current = this.flattedQuestions[i];
        if (current.id === this.question.id) {
          this.selectQuestion(this.flattedQuestions[i + 1]);
          return;
        }
      }
    }
  }

  @autobind
  public showWorkProfileDialog() {
    this.workProfileDialogVisible = true;
  }

  @autobind
  public hideWorkProfileDialog() {
    this.workProfileDialogVisible = false;
  }

  @autobind
  public saveProfile(title: string, summary: string) {
    return this.examService.saveCreationTitleAndSummary(
      title,
      summary,
      this.question
    );
  }
}
